import * as React from "react";
import { useState } from "react";

export type ReplicateItem = {
  name: React.ReactNode;
  sampleName: string;
  runDate: string;
  values: (string | number)[];
};

export type ReplicateHeader = {
  label: string;
  isNumber: boolean;
  fractionDigits?: number;
};

export type ReplicateData = {
  items: ReplicateItem[];
  headers: ReplicateHeader[];
};

export const ReplicateGroup = ({
  data: { headers, items },
  explanation,
}: {
  data: ReplicateData;
  explanation?: React.ReactNode;
}) => {
  const [activeColumn, setActiveColumn] = useState<number | null>(null);

  function onCellMouseOver(colIdx: number) {
    setActiveColumn(colIdx);
  }

  function onCellMouseOut(colIdx: number) {
    setActiveColumn((prevIdx) => (colIdx === prevIdx ? null : prevIdx));
  }

  const replicates: React.ReactNode[] = [];
  for (let i = 0; i < items.length; ++i) {
    const item = items[i];
    replicates.push(
      <Replicate
        key={i}
        activeColumn={activeColumn}
        item={item}
        headers={headers}
        onCellMouseOver={onCellMouseOver}
        onCellMouseOut={onCellMouseOut}
      />
    );
  }
  return (
    <div className="replicate-group">
      <table className="replicates">
        <thead>
          <tr>
            <th>Biological Replicate #</th>
            <th>Sample Name</th>
            <th>Run Date</th>
            {headers.map((h) => (
              <th key={h.label} className={h.isNumber ? "replicates__is-num" : ""}>
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{replicates}</tbody>
      </table>
      {explanation ? (
        <div className="replicate-group__explanation">
          <sup>*</sup>
          {explanation}
        </div>
      ) : null}
    </div>
  );
};

export const Replicate = ({
  item,
  headers,
  activeColumn,
  onCellMouseOver,
  onCellMouseOut,
}: {
  item: ReplicateItem;
  headers: ReplicateHeader[];
  activeColumn: number | null;
  onCellMouseOver: (rowIdx: number) => void;
  onCellMouseOut: (rowIdx: number) => void;
}) => {
  return (
    <tr>
      <td
        onMouseOver={() => onCellMouseOver(-3)}
        onMouseOut={() => onCellMouseOut(-3)}
        className={"replicates__name" + (activeColumn === -3 ? " replicates__active-col" : "")}
      >
        {item.name}
      </td>
      <td
        onMouseOver={() => onCellMouseOver(-2)}
        onMouseOut={() => onCellMouseOut(-2)}
        className={
          "replicates__sampleName" + (activeColumn === -2 ? " replicates__active-col" : "")
        }
      >
        {item.sampleName}
      </td>
      <td
        onMouseOver={() => onCellMouseOver(-1)}
        onMouseOut={() => onCellMouseOut(-1)}
        className={"replicates__date" + (activeColumn === -1 ? " replicates__active-col" : "")}
      >
        {item.runDate}
      </td>
      {item.values.map((val, i) => (
        <td
          key={headers[i].label}
          className={
            (headers[i].isNumber ? "replicates__is-num" : "") +
            (activeColumn === i ? " replicates__active-col" : "")
          }
          onMouseOver={() => onCellMouseOver(i)}
          onMouseOut={() => onCellMouseOut(i)}
        >
          <span>{valueToString(val, headers[i].fractionDigits)}</span>
        </td>
      ))}
    </tr>
  );
};

function valueToString(val: number | string, fractionDigits?: number): string {
  if (typeof val === "string") {
    return val;
  }

  if (fractionDigits !== undefined) {
    return val.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  if (val % 1 === 0) {
    return val.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return val.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
